import { GOODS } from '@nine/shared';
import { coin, drop, leaf, sprite } from '../kit';
import { C, type Hex, mix, type Pix } from '../pix';
import { defineIconKind, registerIcons } from '../store';

/**
 * Goods (วัตถุดิบ เมล็ดพันธุ์ อาหาร ยา): the tile by kind (materials wood-brown, seeds green, food
 * warm orange, medicine jade), the picture the good itself.
 */

const KIND_TINT: Readonly<Record<string, Hex>> = {
  material: '#b08a5a',
  seed: '#7ab04a',
  food: '#e8984a',
  medicine: '#4ac8a0',
};

defineIconKind('good', {
  label: 'วัตถุดิบ อาหาร และยา',
  ids: () => Object.keys(GOODS),
  name: (id) => GOODS[id]?.name ?? id,
  tint: (id) => KIND_TINT[GOODS[id]?.kind ?? 'material'] ?? KIND_TINT.material!,
  plate: 'square',
  fallback: () => ({ draw: (p) => sack(p, '#d8c8a0') }),
});

// ── pieces ──

function log(p: Pix, bark: Hex, ring: Hex): void {
  p.thick(6, 22, 22, 10, bark, 7);
  p.ell(24, 10, 4, 4.4, ring);
  p.ring(24, 10, 2, 2.2, mix(ring, C.ink, 0.3));
  p.px(24, 10, mix(ring, C.ink, 0.3));
  for (const [x, y] of [
    [9, 19],
    [14, 15],
    [18, 12],
  ] as const)
    p.rect(x, y, 3, 1, mix(bark, C.ink, 0.35));
}

function plank(p: Pix, col: Hex): void {
  for (let k = 0; k < 3; k++) {
    p.rect(5, 9 + k * 6, 22, 5, k % 2 ? mix(col, C.ink, 0.12) : col);
    p.rect(5, 13 + k * 6, 22, 1, mix(col, C.ink, 0.4));
    p.line(8 + k * 4, 11 + k * 6, 14 + k * 4, 11 + k * 6, mix(col, C.white, 0.3));
  }
}

function ore(p: Pix, rock: Hex, fleck: Hex): void {
  p.poly(
    [
      [5, 24],
      [8, 12],
      [16, 7],
      [25, 11],
      [28, 22],
      [20, 27],
      [10, 27],
    ],
    rock,
  );
  for (const [x, y] of [
    [12, 14],
    [19, 12],
    [16, 20],
    [23, 18],
    [10, 21],
  ] as const)
    p.rect(x, y, 2, 2, fleck);
}

function fish(p: Pix, body: Hex, fin: Hex, o: { spots?: boolean; cooked?: boolean } = {}): void {
  p.ell(15, 16, 10, 5.5, body);
  p.poly(
    [
      [25, 16],
      [30, 10],
      [30, 22],
    ],
    fin,
  );
  p.poly(
    [
      [11, 11],
      [17, 7],
      [19, 11],
    ],
    fin,
  );
  p.ell(8, 15, 1.4, 1.4, C.white);
  p.px(8, 15, C.ink);
  p.line(11, 13, 11, 19, mix(body, C.ink, 0.3));
  if (o.spots)
    for (const [x, y] of [
      [15, 14],
      [19, 17],
      [17, 19],
    ] as const)
      p.px(x, y, mix(body, C.ink, 0.45));
  if (o.cooked) for (const x of [14, 18, 22]) p.line(x, 12, x - 2, 20, mix(body, C.ink, 0.45));
}

function sack(p: Pix, col: Hex, mark?: Hex): void {
  p.ell(16, 20, 10, 8, col);
  p.rect(12, 8, 8, 5, col);
  p.rect(11, 12, 10, 2, C.wood);
  p.rect(13, 6, 6, 2, mix(col, C.white, 0.25));
  if (mark) p.ell(16, 21, 3, 3, mark);
}

function seeds(p: Pix, bag: Hex, seed: Hex): void {
  sack(p, bag, seed);
  for (const [x, y] of [
    [6, 27],
    [24, 26],
    [27, 22],
  ] as const)
    p.ell(x, y, 1.4, 1, seed);
}

function bowl(p: Pix, col: Hex, fill: Hex): void {
  p.poly(
    [
      [4, 15],
      [28, 15],
      [24, 25],
      [8, 25],
    ],
    col,
  );
  p.rect(11, 25, 10, 2, mix(col, C.ink, 0.25));
  p.ell(16, 15, 12, 2.5, fill);
  p.line(7, 19, 25, 19, mix(col, C.ink, 0.15));
}

function steam(p: Pix): void {
  for (const x of [11, 16, 21])
    for (let y = 4; y < 11; y++) p.px(x + Math.round(Math.sin(y / 1.5 + x)), y, C.white);
}

function ingot(p: Pix, col: Hex): void {
  // A boat-shaped ingot (as cast in a mould).
  p.poly(
    [
      [4, 16],
      [28, 16],
      [24, 25],
      [8, 25],
    ],
    col,
  );
  p.poly(
    [
      [8, 16],
      [12, 10],
      [20, 10],
      [24, 16],
    ],
    mix(col, C.white, 0.15),
  );
  p.line(13, 11, 19, 11, mix(col, C.white, 0.5));
}

function bolt(p: Pix, col: Hex, pattern?: Hex): void {
  // A bolt of cloth, rolled.
  p.rect(6, 9, 20, 16, col);
  p.ell(6, 17, 3, 8, mix(col, C.white, 0.25));
  p.ell(6, 17, 1.2, 3, mix(col, C.ink, 0.4));
  p.rect(26, 9, 2, 16, mix(col, C.ink, 0.25));
  if (pattern)
    for (let y = 11; y < 24; y += 4) for (let x = 10; x < 25; x += 4) p.px(x + (y % 8 ? 2 : 0), y, pattern);
}

function pill(p: Pix, col: Hex, box: Hex): void {
  p.rect(6, 17, 20, 10, box);
  p.rect(6, 17, 20, 2, mix(box, C.white, 0.3));
  p.ell(16, 13, 6, 6, col);
  p.px(14, 10, C.white).px(13, 11, C.white);
}

registerIcons('good', {
  'pine-log': (p) => log(p, '#8a5a3a', '#e8c890'),
  'hard-wood': (p) => plank(p, '#a86a3a'),
  'iron-ore': (p) => ore(p, '#7a7068', '#d8a070'),
  'black-iron': (p) => ore(p, '#3a3640', '#8a9ab8'),
  carp: (p) => fish(p, '#e8a040', '#e8703a'),
  'mandarin-fish': (p) => fish(p, '#a8b878', '#7a8a4a', { spots: true }),
  'herb-leaf': (p) => {
    leaf(p, 8, 25, 1, -1.2, 18, '#5cc04a');
    leaf(p, 14, 26, 1.4, -0.5, 14, '#7ad85a');
    p.line(8, 25, 18, 13, '#3a8a2a');
  },
  lingzhi: (p) => {
    p.ell(16, 13, 12, 6, '#a8402a');
    p.ell(16, 12, 9, 4, '#c8603a');
    p.ring(16, 13, 6, 3, '#e8a060', Math.PI, Math.PI * 2);
    p.rect(14, 17, 4, 10, '#d8b890');
  },
  rice: (p) => {
    p.line(16, 28, 16, 8, '#c8a050');
    for (let k = 0; k < 7; k++) {
      p.ell(13 - (k % 2), 8 + k * 2.5, 2, 1.3, '#f0d070');
      p.ell(19 + (k % 2), 9 + k * 2.5, 2, 1.3, '#e8c060');
    }
    leaf(p, 16, 24, -1, -0.4, 9, '#8ac050');
  },
  straw: (p) => {
    for (let x = 8; x < 25; x += 2) p.line(x, 6, x + (x < 16 ? 2 : -2), 27, x % 4 ? '#e8d070' : '#c8a850');
    p.rect(8, 15, 17, 3, '#8a6a3a');
  },
  cabbage: (p) => {
    p.ell(16, 18, 11, 9, '#7ac05a');
    p.ell(16, 16, 7, 6, '#b8e890');
    for (const a of [-2.4, -0.7]) p.line(16, 18, 16 + Math.cos(a) * 9, 18 + Math.sin(a) * 8, '#e8f8d0');
  },
  cotton: (p) => {
    p.line(16, 28, 16, 18, '#6a4a2a');
    for (const [x, y] of [
      [11, 14],
      [21, 14],
      [16, 10],
      [16, 17],
    ] as const)
      p.ell(x, y, 5, 4.5, C.white);
    p.poly(
      [
        [11, 21],
        [16, 17],
        [21, 21],
      ],
      '#8a5a3a',
    );
  },
  'rice-seed': (p) => seeds(p, '#d8c8a0', '#f0d070'),
  'cabbage-seed': (p) => seeds(p, '#c8d8a0', '#6a8a3a'),
  'cotton-seed': (p) => seeds(p, '#e8e0d0', '#5a4030'),
  'silk-thread': (p) => {
    p.rect(9, 8, 14, 18, '#e8d0f0');
    for (let y = 9; y < 26; y += 2) p.rect(9, y, 14, 1, '#c8a8e0');
    p.rect(7, 6, 18, 3, C.wood).rect(7, 25, 18, 3, C.wood);
    p.line(23, 16, 29, 22, '#e8d0f0');
  },
  charcoal: (p) => {
    for (const [x, y, w, h] of [
      [5, 16, 12, 8],
      [14, 12, 13, 8],
      [10, 20, 14, 7],
    ] as const)
      p.rect(x, y, w, h, '#3a3438');
    for (const [x, y] of [
      [8, 18],
      [18, 14],
      [16, 23],
    ] as const)
      p.px(x, y, '#ff8a4a');
  },
  'iron-ingot': (p) => ingot(p, '#a8b0bc'),
  'black-ingot': (p) => ingot(p, '#4a4a58'),
  'cotton-cloth': (p) => bolt(p, '#f0ead8'),
  'indigo-dye': (p) => {
    p.poly(
      [
        [9, 10],
        [23, 10],
        [26, 26],
        [6, 26],
      ],
      '#c8a070',
    );
    p.rect(8, 8, 16, 3, '#a87a4a');
    p.ell(16, 10, 6.5, 1.5, '#2a4aa8');
    drop(p, 25, 8, 2, '#3a5ad8');
  },
  'silk-cloth': (p) => bolt(p, '#c84a8a', '#ffc8e0'),
  'rice-porridge': (p) => {
    steam(p);
    bowl(p, '#e8eef8', '#f8f4e8');
  },
  'grilled-carp': (p) => {
    p.line(2, 28, 30, 6, C.wood);
    fish(p, '#c8783a', '#a8582a', { cooked: true });
  },
  'cabbage-soup': (p) => {
    steam(p);
    bowl(p, '#c8d8f0', '#9ad070');
  },
  'mandarin-feast': (p) => {
    p.ell(16, 21, 14, 6, '#f0f4f8');
    p.ell(16, 21, 12, 4.5, '#d8e8f0');
    sprite(
      p,
      6,
      14,
      ['...ff........', '.fffffffffff.', 'fwfffffffffff', '.fffffffffff.', '...ff....f.f.'],
      { f: '#e8703a', w: C.white },
      { k: 1 },
    );
    steam(p);
  },
  'heal-pill': (p) => pill(p, '#e84a4a', '#c8a070'),
  'qi-pill': (p) => pill(p, '#4a8ae8', '#c8a070'),
  'lingzhi-elixir': (p) => {
    // A gourd of elixir with a red cord.
    p.ell(16, 21, 8, 7, '#d8a050');
    p.ell(16, 11, 5, 5, '#d8a050');
    p.rect(14, 4, 4, 3, '#a8402a');
    p.rect(9, 15, 14, 1, C.seal);
    coin(p, 16, 21, 3, '#c8402a');
  },
});

export { KIND_TINT as GOOD_TINT };
